import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  LoadedContent,
  TableContainer,
  TableHeaderBulkSelectCheckbox,
} from '@occc-app/design-system';
import {
  IPaginationProps,
  PaginationHandler,
  PaginationInfo,
} from '@occc-app/hooks/usePaginatedQuery';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { Cpg, SubmissionGroupLocationFragment } from '@occc/graphql';

import { SubmissionGroupLocationsTableRow } from './submission-group-locations-table-row';
import { CpgAccountHeaderCells } from './cpg-account-cells';
import { isLocationSelectableForCpg } from './submission-group-locations-table.utils';

type SubmissionGroupLocationsTableProps = ReturnType<typeof useBulkSelect> & {
  cpg: Cpg;
  loading: boolean;
  error?: ApolloError;
  data?: SubmissionGroupLocationFragment[];
  paginationProps: IPaginationProps;
  paginationInfo?: PaginationInfo;
  onPageChange?: PaginationHandler;
};

export const SubmissionGroupLocationsTable = ({
  cpg,
  loading,
  error,
  data,
  paginationProps,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleItemSelect,
}: SubmissionGroupLocationsTableProps) => {
  const selectablePageLocationUuids = useMemo(
    () => data?.filter((loc) => isLocationSelectableForCpg(loc, cpg)).map((loc) => loc.uuid) || [],
    [data, cpg],
  );

  const renderData = (items: SubmissionGroupLocationFragment[]) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>
              <TableHeaderBulkSelectCheckbox
                bulkSelectedItems={bulkSelectedItems}
                handleBulkSelectMultiple={handleBulkSelectMultiple}
                currentPageItems={selectablePageLocationUuids}
              />
            </TableCell>
            <TableCell>Site ID</TableCell>
            <TableCell>Site Name (DBA)</TableCell>
            <CpgAccountHeaderCells cpg={cpg} />
            <TableCell>Site Address</TableCell>
            <TableCell>Site Phone Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((location) => (
            <SubmissionGroupLocationsTableRow
              cpg={cpg}
              location={location}
              key={location.uuid}
              isItemSelected={bulkSelectedItems.has(location.uuid)}
              handleItemSelect={handleItemSelect}
            />
          ))}
        </TableBody>
      </Table>
      <Pagination {...paginationProps} />
    </TableContainer>
  );

  return (
    <LoadedContent<SubmissionGroupLocationFragment>
      error={error}
      loading={loading}
      items={data}
      renderData={renderData}
    />
  );
};
